import { FileIcon, defaultStyles } from "react-file-icon";

import ProductOverview from "../product-detail-page/components/ProductOverview";

import ProductDetailSheetLoader from "./ProductDetailSheetLoader";

import Avatar from "@/components/reusables/Avatar";
import FetchWrapper from "@/components/reusables/FetchWrapper";
import { H3 } from "@/components/reusables/typography";
import EmptyData from "@/components/state-pages/EmptyData";
import { Badge } from "@/components/ui/badge";
import * as Sheet from "@/components/ui/sheet";
import { allowedImageTypes, imgBaseUrl } from "@/constants/constants";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import { ProductType } from "@/interfaces/tools.interface";
import {
  openFilePreviewInNewTab,
  splitImageFileName,
} from "@/lib/file-image.utils";
import { cn, formatToCapitalizedWords } from "@/lib/utils";
import { useGetSingleProductQuery } from "@/redux/api/toolsQuery";

interface ProductDetailSheetProps {
  isOpen: boolean;
  toggleProductDetailSheet: () => void;
}

const isFileAndImage = (fileExtension: string): boolean => {
  return allowedImageTypes.includes(fileExtension) ?? false;
};

const generateRandomSize = (): number => {
  const sizes = [100, 200, 300, 400, 500];

  return sizes[Math.floor(Math.random() * sizes.length)];
};

export const Attachments = ({
  filesAndImages,
}: {
  filesAndImages: string[];
}): JSX.Element => {
  return (
    <section className='border border-input rounded-sm p-4 space-y-[14px]'>
      {filesAndImages.map((file, index) => {
        const fileExtension = file.split(".").pop() || "pdf";
        const fileName = splitImageFileName(file, imgBaseUrl);

        const handlePreview = (): void => {
          if (isFileAndImage(fileExtension)) {
            return;
          }

          openFilePreviewInNewTab(file);
        };

        return (
          <div
            key={index}
            className='px-2 py-[6px] border border-input rounded-sm flex items-center gap-3'
          >
            <div
              className={cn(
                "h-[40px] w-[40px]",
                isFileAndImage(fileExtension) && "mb-2",
              )}
            >
              {isFileAndImage(fileExtension) ? (
                <Avatar src={file} name='image' className='rounded' />
              ) : (
                <FileIcon
                  extension={fileExtension}
                  className='pb-6'
                  // @ts-expect-error - Ignore type error
                  {...defaultStyles[fileExtension]}
                />
              )}
            </div>
            <div className='flex w-full items-center justify-between'>
              <span
                className={cn(
                  "truncate",
                  !isFileAndImage(fileExtension) &&
                    "underline text-primary cursor-pointer",
                )}
                onClick={handlePreview}
              >
                {fileName}
              </span>
              <span className='text-sm text-muted-foreground'>
                {generateRandomSize()}KB
              </span>
            </div>
          </div>
        );
      })}
    </section>
  );
};

const ProductDetailSheet = ({
  isOpen,
  toggleProductDetailSheet,
}: ProductDetailSheetProps): JSX.Element => {
  const { id, reset } = useQueryID(ToolsKeys.PRODUCT_ASSIGNMENT_ID);
  const { data, ...rest } = useGetSingleProductQuery({
    id,
  });
  const productData = data?.data;
  const productType = productData?.type;

  const handleExit = (): void => {
    reset();
    toggleProductDetailSheet();
  };

  return (
    <Sheet.Sheet open={isOpen} onOpenChange={handleExit}>
      <Sheet.SheetContent className='min-w-[38dvw] min-h-screen overflow-scroll'>
        <FetchWrapper {...rest} loaderComp={<ProductDetailSheetLoader />}>
          <section className='space-y-5'>
            <section className=' border border-input p-4 rounded-md space-y-[14px]'>
              <div className='flex items-center gap-4'>
                <Avatar
                  src={productData?.images[0]}
                  name={productData?.name || ""}
                  className='!w-16 !h-16 '
                />
                <div>
                  <h1 className='text-2xl font-semibold'>
                    {productData?.name}
                  </h1>
                  <p className='text-sm text-gray-400'>
                    {formatToCapitalizedWords(productType || "")}
                  </p>
                </div>
              </div>
              <div>
                {productType === ProductType.Has_Sub_Product ? (
                  <div className='space-x-2'>
                    <Badge variant={"secondary"}>
                      Total Quantity :{" "}
                      {productData?._count
                        ? Object.values(productData._count).reduce(
                            (sum, value) => (sum as number) + (value as number),
                            0,
                          )
                        : 0}
                    </Badge>
                  </div>
                ) : (
                  <div className='space-x-2'>
                    <Badge variant={"secondary"}>
                      Total Quantity : {productData?.quantity || 0}
                    </Badge>
                    <Badge variant={"secondary"}>
                      Onsite: {productData?._count.onsite || 0}
                    </Badge>
                  </div>
                )}
              </div>
            </section>
            <section>
              {productType === ProductType.Has_Sub_Product && (
                <ProductOverview
                  className='grid-cols-4'
                  data={[
                    {
                      label: ToolsConstants.PRODUCTS.AVAILABLE,
                      value: productData?._count?.available || 0,
                    },
                    {
                      label: ToolsConstants.PRODUCTS.SENT_TO_SITE,
                      value: productData?._count?.sentToSite || 0,
                    },
                    {
                      label: ToolsConstants.PRODUCTS.ON_SITE,
                      value: productData?._count.onsite || 0,
                    },
                    {
                      label: ToolsConstants.PRODUCTS.CONSUMNED,
                      value: productData?._count?.consumed || 0,
                    },
                  ]}
                />
              )}
            </section>
            <section className='space-y-2'>
              <H3>{ToolsConstants.PRODUCTS.FILES_AND_ATTACHMENTS}</H3>
              {productData?.images.length || productData?.files.length ? (
                <Attachments
                  filesAndImages={[
                    ...(productData?.images || []),
                    ...(productData?.files || []),
                  ]}
                />
              ) : (
                <EmptyData title='files or attachments' />
              )}
            </section>
          </section>
        </FetchWrapper>
      </Sheet.SheetContent>
    </Sheet.Sheet>
  );
};

export default ProductDetailSheet;
